import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

export enum BeaconSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export enum BeaconVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  WARNING = "WARNING",
  LIME = "LIME",
  YELLOW = "YELLOW",
  BLUE = "BLUE",
  ORANGE = "ORANGE",
  PINK = "PINK",
  PURPLE = "PURPLE",
  TEAL = "TEAL",
  DISABLED = "DISABLED",
}

export interface BeaconProps {
  /**
   * The size of the Beacon.
   */
  size?: BeaconSize;
  /**
   * The color of the Beacon.
   */
  variant?: BeaconVariant;
  /**
   * The speed of the pulse animation in seconds.
   * @default 2
   */
  speed?: number;
  /**
   * Whether to show the pulse animation.
   */
  isPulse?: boolean;
}

const BeaconWrapper = styled.div<{ $size: BeaconSize }>`
  position: relative;
  width: ${({ $size }) =>
    match($size)
      .returnType<string>()
      .with(BeaconSize.SMALL, () => "6px")
      .with(BeaconSize.MEDIUM, () => "8px")
      .with(BeaconSize.LARGE, () => "10px")
      .exhaustive()};
  height: ${({ $size }) =>
    match($size)
      .returnType<string>()
      .with(BeaconSize.SMALL, () => "6px")
      .with(BeaconSize.MEDIUM, () => "8px")
      .with(BeaconSize.LARGE, () => "10px")
      .exhaustive()};

  display: flex;
  align-items: center;
  justify-content: center;
`;

const BeaconPulse = withTheme(styled.span<
  PropsWithTheme<{
    $variant: BeaconVariant;
    $speed?: number;
  }>
>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: ping ${({ $speed }) => $speed ?? 2}s cubic-bezier(0, 0, 0.1, 0.1) infinite;
  opacity: 0.5;

  background-color: ${({ $variant, theme }) => {
    return match($variant)
      .returnType<string>()
      .with(BeaconVariant.BASE, () => theme.color.icon.primary)
      .with(BeaconVariant.PRIMARY, () => theme.color.icon.primary)
      .with(BeaconVariant.SECONDARY, () => theme.color.icon.secondary)
      .with(BeaconVariant.TERTIARY, () => theme.color.background.tertiaryAlt)
      .with(BeaconVariant.SUCCESS, () => theme.color.border.success)
      .with(BeaconVariant.ERROR, () => theme.color.border.error)
      .with(BeaconVariant.WARNING, () => theme.color.border.warning)
      .with(BeaconVariant.LIME, () => theme.color.background.limeAlt)
      .with(BeaconVariant.YELLOW, () => theme.color.background.yellowAlt)
      .with(BeaconVariant.BLUE, () => theme.color.icon.blue)
      .with(BeaconVariant.ORANGE, () => theme.color.background.orangeAlt)
      .with(BeaconVariant.PINK, () => theme.color.background.pinkAlt)
      .with(BeaconVariant.PURPLE, () => theme.color.background.purpleAlt)
      .with(BeaconVariant.TEAL, () => theme.color.background.tealAlt)
      .with(BeaconVariant.DISABLED, () => theme.color.background.disabled)
      .exhaustive();
  }};

  @keyframes ping {
    75%,
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`);

const BeaconDot = withTheme(styled.span<
  PropsWithTheme<{
    $variant: BeaconVariant;
  }>
>`
  z-index: 1;
  width: 70%;
  height: 70%;
  border-radius: 50%;

  background-color: ${({ $variant, theme }) => {
    return match($variant)
      .returnType<string>()
      .with(BeaconVariant.BASE, () => theme.color.background.baseAlt)
      .with(BeaconVariant.PRIMARY, () => theme.color.background.primaryAlt)
      .with(BeaconVariant.SECONDARY, () => theme.color.background.secondaryAlt)
      .with(BeaconVariant.TERTIARY, () => theme.color.background.tertiaryAlt)
      .with(BeaconVariant.SUCCESS, () => theme.color.icon.success)
      .with(BeaconVariant.ERROR, () => theme.color.icon.error)
      .with(BeaconVariant.WARNING, () => theme.color.icon.warning)
      .with(BeaconVariant.LIME, () => theme.color.background.limeAlt)
      .with(BeaconVariant.YELLOW, () => theme.color.background.yellowAlt)
      .with(BeaconVariant.BLUE, () => theme.color.icon.blue)
      .with(BeaconVariant.ORANGE, () => theme.color.background.orangeAlt)
      .with(BeaconVariant.PINK, () => theme.color.background.pinkAlt)
      .with(BeaconVariant.PURPLE, () => theme.color.background.purpleAlt)
      .with(BeaconVariant.TEAL, () => theme.color.background.tealAlt)
      .with(BeaconVariant.DISABLED, () => theme.color.border.disabled)
      .exhaustive();
  }};
`);

const Beacon = ({
  variant = BeaconVariant.BASE,
  size = BeaconSize.MEDIUM,
  speed,
  isPulse,
}: BeaconProps) => (
  <BeaconWrapper $size={size}>
    {isPulse && <BeaconPulse $variant={variant} $speed={speed} />}
    <BeaconDot $variant={variant} />
  </BeaconWrapper>
);

export default Beacon;
